import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Grid } from '@material-ui/core';
import type { Theme } from '@material-ui/core';

import { useCurrentUser } from '@klp/auth';
import { ClientRoutes } from '@klp/client/config';
import { Conditional } from '@klp/conditional';
import { AppRoutes } from '@klp/core/app/config';
import { useAppParams } from '@klp/core/app/useAppParams';
import { ActionLink, Block, Heading } from '@klp/design';
import { Loader } from '@klp/design/Loader';
import { useKlpStyles } from '@klp/design/Theme';
import { ErrorNotification } from '@klp/errorNotification';
import { Main } from '@klp/layout';

import { BuildingTabs } from '../classes';
import { ByggSingleRoutes } from '../components/ByggSingle/ByggSingleRoutes';
import { ByggSingleTabs } from '../components/ByggSingle/ByggSingleTabs';
import { ByggAccess, ByggRoutes } from '../config';
import { useGetByggQuery } from '../rtkApi';

const byggSingleStyles = (theme: Theme) => ({
    appBar: {
        marginTop: '30px',
        marginBottom: '30px',
    },
    tab: {
        fontWeight: 400,
    },
    tabHidden: {
        display: 'none',
    },
    iconTextContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    iconSpacing: {
        marginRight: theme.spacing(1),
    },
});

export const ByggSingle: React.FC = () => {
    const classes = useKlpStyles({}, byggSingleStyles);
    const history = useHistory();
    const params = useAppParams();

    const { currentUser: user, currentUserIsLoading: userIsLoading } =
        useCurrentUser();

    const { byggNummer, tab, selectedClient, isAdmin } = params;

    // The API only finds byggNummer in uppercase (e.g. N20101)
    const isCanonicalByggNummer = byggNummer === byggNummer?.toUpperCase();

    const {
        data: bygg,
        isLoading: byggLoading,
        error: byggError,
    } = useGetByggQuery(
        { byggNummer: byggNummer! },
        { skip: !byggNummer || !isCanonicalByggNummer },
    );

    // Handle initial tab redirect and uppercase byggNummer
    useEffect(() => {
        if (!byggNummer) return;
        const isValidTab =
            !!tab && Object.values(BuildingTabs).includes(tab as BuildingTabs);
        if (isValidTab && isCanonicalByggNummer) return;

        history.replace(
            ByggRoutes.single(
                byggNummer.toUpperCase(),
                isValidTab ? (tab as BuildingTabs) : BuildingTabs.overview,
            ),
        );
    }, [byggNummer, tab, isCanonicalByggNummer, history]);

    return (
        <Main>
            <Loader isLoading={byggLoading || userIsLoading}>
                <Conditional
                    condition={ByggAccess(user) || !!selectedClient}
                    errorComponent={
                        <ErrorNotification
                            title="clientSelect.errorNoClientsSelected.title"
                            description="clientSelect.errorNoClientsSelected.description"
                            backTarget={AppRoutes.mainPage()}
                            backTitle="general.goBackToFrontpage"
                            mainTarget={ClientRoutes.clientSelect()}
                            mainTitle="clientSelect.chooseClient"
                            returnUrl={true}
                        />
                    }
                >
                    <Conditional
                        condition={!byggError}
                        errorComponent={
                            <ErrorNotification
                                title="bygg.errorSingle.title"
                                description="bygg.errorSingle.description"
                                backTarget={AppRoutes.mainPage()}
                                backTitle="general.goBackToFrontpage"
                            />
                        }
                    >
                        <Block marginBottom="jumbo">
                            {bygg && (
                                <Grid container={true}>
                                    <Grid item={true} xs={12}>
                                        {isAdmin && selectedClient && (
                                            <Heading klpHeadingLevel="TWO">
                                                {selectedClient.kundeName}
                                            </Heading>
                                        )}
                                        <Heading klpHeadingLevel="ONE">
                                            {bygg.navn}
                                        </Heading>
                                    </Grid>

                                    {isAdmin && (
                                        <Grid container={true} spacing={2}>
                                            <Grid
                                                item={true}
                                                xs={12}
                                                sm={6}
                                                md={8}
                                                lg={12}
                                            >
                                                <Grid
                                                    container={true}
                                                    justifyContent="flex-start"
                                                >
                                                    <Grid item={true}>
                                                        <ActionLink
                                                            actionType="link"
                                                            translation="bygg.showAll"
                                                            href={ByggRoutes.listAll(
                                                                byggNummer,
                                                            )}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )}

                                    <Grid item={true} xs={12}>
                                        <ByggSingleTabs
                                            byggNummer={byggNummer}
                                            tab={tab}
                                            user={user}
                                            selectedClient={selectedClient}
                                            isAdmin={isAdmin}
                                            classes={classes}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                            <Grid item={true} xs={12}>
                                {bygg && <ByggSingleRoutes bygg={bygg} />}
                            </Grid>
                        </Block>
                    </Conditional>
                </Conditional>
            </Loader>
        </Main>
    );
};

ByggSingle.displayName = 'ByggSingle';
